/**
 * Quiz Context — Centralized quiz state management
 * 
 * Provides unified quiz store for all quiz screens.
 * Handles loading/saving from localStorage (offline-first).
 * 
 * Features:
 * - Single source of truth for quiz state
 * - Auto-persist on changes
 * - Resume capability
 * - Step navigation helpers
 */

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { useAuth } from '@/hooks/useAuth';
import { loadQuizDraft, saveQuizDraft, clearQuizDraft, type QuizState, type QuizStepAnswer } from '@/lib/services/quizRepository';
import { loadQuizConfig, type QuizConfig, type QuizStepConfig, getTotalSteps } from '@/lib/services/quizConfigService';
import { analyticsService } from '@/lib/services/analyticsService';
import { logger } from '@/lib/services/logger';

// Import for internal use and re-export for backwards compatibility
import { QUIZ_STEP_ROUTES, TOTAL_MAIN_STEPS, getRouteForStep as getRouteForStepUtil } from '@/lib/quiz/quizRoutes';
export { QUIZ_STEP_ROUTES, TOTAL_MAIN_STEPS } from '@/lib/quiz/quizRoutes';


export interface ExtendedQuizState extends QuizState {
  processingStatus: 'idle' | 'loading' | 'success' | 'error';
}

export { type QuizConfig, type QuizStepConfig } from '@/lib/services/quizConfigService';

export interface QuizContextValue {
  /** Current quiz state */
  state: ExtendedQuizState;
  /** Dynamic quiz config */
  quizConfig: QuizConfig | null;
  /** Whether quiz draft is being loaded */
  isLoading: boolean;
  /** Whether there's a resumable draft */
  hasDraft: boolean;
  /** Set answer for a step */
  setAnswer: (step: number, answer: QuizStepAnswer | string) => void;
  /** Go to next step */
  nextStep: () => void;
  /** Go to previous step */
  prevStep: () => void;
  /** Navigate to specific step */
  goToStep: (step: number) => void;
  /** Clear quiz and start fresh */
  resetQuiz: () => Promise<void>;
  /** Resume quiz from saved draft */
  resumeQuiz: () => void;
  /** Get route for a step */
  getRouteForStep: (step: number) => string;
  /** Mark quiz as processing */
  setProcessingStatus: (status: 'idle' | 'loading' | 'success' | 'error') => void;
  /** Get step config by number */
  getStepConfig: (stepNumber: number) => QuizStepConfig | undefined;
}

const DEFAULT_STATE: ExtendedQuizState = {
  currentStep: 0,
  totalSteps: TOTAL_MAIN_STEPS,
  answers: {},
  updatedAt: Date.now(),
  processingStatus: 'idle',
};

const QuizContext = createContext<QuizContextValue | undefined>(undefined);

interface QuizProviderProps {
  children: ReactNode;
}

export function QuizProvider({ children }: QuizProviderProps) {
  const navigate = useSafeNavigate();
  const { user } = useAuth();
  const [state, setState] = useState<ExtendedQuizState>(DEFAULT_STATE);
  const [quizConfig, setQuizConfig] = useState<QuizConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasDraft, setHasDraft] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Load config and draft on mount
  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        // Load quiz config (dynamic steps)
        const config = await loadQuizConfig();
        setQuizConfig(config);
        
        // Update totalSteps from config
        const totalSteps = getTotalSteps(config);
        setState(prev => ({ ...prev, totalSteps }));
        
        // Load draft
        const draft = await loadQuizDraft(user?.id);
        if (draft) {
          // Hydrate in-memory state from saved draft so QuizRunner hooks work
          setState(prev => ({
            ...prev,
            ...draft,
            processingStatus: 'idle',
          }));

          if (Object.keys(draft.answers).length > 0 && draft.currentStep > 0) {
            setHasDraft(true);
            logger.debug('Quiz draft found', {
              data: { currentStep: draft.currentStep, answersCount: Object.keys(draft.answers).length },
              allowList: ['currentStep', 'answersCount'],
            });
          } else {
            setHasDraft(false);
          }
        } else {
          setHasDraft(false);
        }
      } catch (error) {
        logger.error('Failed to load quiz data', { error });
      } finally {
        setIsLoading(false);
        setInitialized(true);
      }
    };

    loadInitialData();
  }, [user?.id]);

  // Save state to storage on changes (debounced)
  useEffect(() => {
    if (!initialized) return;
    
    const saveState = async () => {
      await saveQuizDraft({
        currentStep: state.currentStep,
        totalSteps: state.totalSteps,
        answers: state.answers,
        updatedAt: Date.now(),
      }, user?.id);
    };

    // Only save if we have meaningful state
    if (state.currentStep > 0 || Object.keys(state.answers).length > 0) {
      const timeoutId = setTimeout(saveState, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [state.currentStep, state.answers, user?.id, initialized]);

  const setAnswer = useCallback((step: number, answer: QuizStepAnswer | string) => {
    setState(prev => ({
      ...prev,
      answers: { ...prev.answers, [step]: answer },
      updatedAt: Date.now(),
    }));
  }, []);

  const getRouteForStep = useCallback((step: number): string => {
    return getRouteForStepUtil(step);
  }, []);

  const nextStep = useCallback(() => {
    setState(prev => {
      const newStep = prev.currentStep + 1;
      return { ...prev, currentStep: newStep };
    });
    
    const newStep = state.currentStep + 1;
    navigate(getRouteForStep(newStep));
    
    analyticsService.track('quiz_next_click', {
      from_step: state.currentStep,
      to_step: newStep,
    }, 'quiz');
  }, [state.currentStep, navigate, getRouteForStep]);

  const prevStep = useCallback(() => {
    if (state.currentStep <= 0) return;
    
    const newStep = state.currentStep - 1;
    setState(prev => ({ ...prev, currentStep: newStep }));
    navigate(getRouteForStep(newStep));
    
    analyticsService.track('quiz_back_click', {
      from_step: state.currentStep,
      to_step: newStep,
    }, 'quiz');
  }, [state.currentStep, navigate, getRouteForStep]);

  const goToStep = useCallback((step: number) => {
    setState(prev => ({ ...prev, currentStep: step }));
    navigate(getRouteForStep(step));
  }, [navigate, getRouteForStep]);

  const resetQuiz = useCallback(async () => {
    await clearQuizDraft(user?.id);
    setState({
      ...DEFAULT_STATE,
      currentStep: 1,
      updatedAt: Date.now(),
    });
    setHasDraft(false);
    
    analyticsService.track('quiz_restart_click', {}, 'quiz');
    
    // Navigate to first question
    navigate('/quiz/step/1');
  }, [user?.id, navigate]);

  const resumeQuiz = useCallback(async () => {
    const draft = await loadQuizDraft(user?.id);
    if (draft) {
      setState({
        ...draft,
        processingStatus: 'idle',
      });
      setHasDraft(false);
      
      analyticsService.track('quiz_resume_click', {
        resumed_step: draft.currentStep,
      }, 'quiz');
      
      // Navigate to the saved step
      navigate(getRouteForStep(draft.currentStep));
    }
  }, [user?.id, navigate, getRouteForStep]);

  const setProcessingStatus = useCallback((status: 'idle' | 'loading' | 'success' | 'error') => {
    setState(prev => ({ ...prev, processingStatus: status }));
  }, []);

  const getStepConfig = useCallback((stepNumber: number): QuizStepConfig | undefined => {
    return quizConfig?.steps.find(s => s.stepNumber === stepNumber);
  }, [quizConfig]);

  const value: QuizContextValue = {
    state,
    quizConfig,
    isLoading,
    hasDraft,
    setAnswer,
    nextStep,
    prevStep,
    goToStep,
    resetQuiz,
    resumeQuiz,
    getRouteForStep,
    setProcessingStatus,
    getStepConfig,
  };

  return <QuizContext.Provider value={value}>{children}</QuizContext.Provider>;
}

export function useQuiz(): QuizContextValue {
  const context = useContext(QuizContext);
  if (context === undefined) {
    throw new Error('useQuiz must be used within a QuizProvider');
  }
  return context;
}

/**
 * Hook to use quiz context safely (returns null if not in provider)
 */
export function useQuizSafe(): QuizContextValue | null {
  return useContext(QuizContext) || null;
}

export default QuizContext;
